import {
  PHOTO_JPEG_QUALITY,
  PHOTO_MAX_BYTES,
  PHOTO_MAX_SIDE_PX,
  PHOTO_TARGET_BYTES,
} from '@barso/shared';

/**
 * Foto für die Kasse verkleinern (U17h, Plan 16 Abschnitt 4.10.9): lange Seite höchstens 1280 px, JPEG mit Qualität 0,7, Ziel
 * höchstens 300 KB, Obergrenze 400 KB. Das Neukodieren über ein Canvas entfernt EXIF und damit auch Ortsdaten: Es wird nie die
 * Originaldatei hochgeladen, immer nur das neu gezeichnete Bild.
 */

/** Zielgröße unter Beibehaltung des Seitenverhältnisses (nie vergrößern, mindestens 1 px). */
export function fitSize(
  width: number,
  height: number,
  maxSide: number = PHOTO_MAX_SIDE_PX,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) throw new RangeError('Das Bild hat keine Größe.');
  const longSide = Math.max(width, height);
  if (longSide <= maxSide) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxSide / longSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Qualitätsstufen: erst 0,7, dann weiter herunter, bis das Foto unter dem Ziel (oder wenigstens unter der Obergrenze) liegt. */
export const QUALITY_STEPS = [PHOTO_JPEG_QUALITY, 0.6, 0.5, 0.4, 0.3] as const;

/**
 * Kodiert mit sinkender Qualität. Gibt das erste Ergebnis unter `targetBytes` zurück, sonst das kleinste unter `maxBytes`,
 * sonst `null` (das Foto wird nicht gespeichert, der Verkauf geht trotzdem).
 */
export async function encodeWithinLimit(
  encode: (quality: number) => Promise<Blob | null>,
  targetBytes: number = PHOTO_TARGET_BYTES,
  maxBytes: number = PHOTO_MAX_BYTES,
): Promise<Blob | null> {
  let smallest: Blob | null = null;
  for (const quality of QUALITY_STEPS) {
    const blob = await encode(quality);
    if (blob === null) continue;
    if (blob.size <= targetBytes) return blob;
    if (smallest === null || blob.size < smallest.size) smallest = blob;
  }
  return smallest !== null && smallest.size <= maxBytes ? smallest : null;
}

/** Enthält das JPEG einen EXIF-Abschnitt (APP1 mit „Exif“)? Nur für Prüfungen; ein Canvas-Ergebnis hat nie einen. */
export function jpegHasExif(bytes: Uint8Array): boolean {
  let offset = 2;
  while (offset + 4 <= bytes.length && bytes[offset] === 0xff) {
    const marker = bytes[offset + 1] ?? 0;
    if (marker === 0xda || marker === 0xd9) return false;
    const length = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    if (
      marker === 0xe1 &&
      bytes[offset + 4] === 0x45 &&
      bytes[offset + 5] === 0x78 &&
      bytes[offset + 6] === 0x69 &&
      bytes[offset + 7] === 0x66
    ) {
      return true;
    }
    offset += 2 + length;
  }
  return false;
}

/** Quelle eines Fotos: ein Bild (Datei) oder ein Videobild der Kamera. */
export type PhotoSource = ImageBitmap | HTMLVideoElement;

const sizeOf = (source: PhotoSource): { width: number; height: number } =>
  'videoWidth' in source
    ? { width: source.videoWidth, height: source.videoHeight }
    : { width: source.width, height: source.height };

/**
 * Zeichnet die Quelle verkleinert auf ein Canvas und kodiert sie als JPEG unter der Grenze. Gibt die Bytes zurück oder `null`
 * (Canvas fehlt, Bild zu groß auch bei niedrigster Qualität). Das Canvas verwirft EXIF.
 */
export async function shrinkToJpeg(source: PhotoSource): Promise<ArrayBuffer | null> {
  const { width, height } = fitSize(sizeOf(source).width, sizeOf(source).height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(source, 0, 0, width, height);
  const blob = await encodeWithinLimit(
    (quality) =>
      new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      }),
  );
  return blob === null ? null : blob.arrayBuffer();
}

/**
 * Einzelbild (`captureStill`) aus dem laufenden Kamerabild der Kasse. Gibt `null` zurück, wenn gerade kein Bild läuft; die
 * Oberfläche bietet dann die Auswahl einer Datei bzw. der Gerätekamera an.
 */
export async function captureStill(video: HTMLVideoElement | null): Promise<ArrayBuffer | null> {
  if (video === null || video.readyState < 2 || video.videoWidth === 0) return null;
  return shrinkToJpeg(video);
}

/** Foto aus einer ausgewählten Datei (Gerätekamera oder Galerie): Drehung laut EXIF wird vor dem Neuzeichnen angewendet. */
export async function shrinkFile(file: Blob): Promise<ArrayBuffer | null> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    return await shrinkToJpeg(bitmap);
  } finally {
    bitmap.close();
  }
}
